import type { SimpleCommandType } from '@bioforge/game-core';
import { Container, Graphics, Rectangle, Text, type FederatedPointerEvent } from 'pixi.js';
import { artSprite } from '../ui/textures.js';
import { COLOR, textStyle } from '../ui/theme.js';
import { vertical } from '../ui/widgets.js';

/**
 * Сенсорное управление (SPEC.md, 16.3): жесты на банке и экранные кнопки — ещё один источник
 * тех же команд движка, что и клавиатура.
 */

export type TouchCommand = Exclude<SimpleCommandType, 'tick'>;

/** Касание короче этого и почти без сдвига — поворот. */
const TAP_MS = 260;
/** Взмах вниз быстрее этого (клеток в секунду) — сброс капсулы. */
const FLICK_CELLS_PER_S = 9;

/**
 * Жесты на банке: ведение пальцем сдвигает капсулу по клеткам, как только палец прошёл клетку;
 * короткое касание поворачивает; быстрый взмах вниз сбрасывает, медленное ведение вниз ускоряет.
 * Время и координаты передаёт экран — так распознавание проверяется тестами.
 */
export class GestureTracker {
  private start: { x: number; y: number; t: number } | null = null;
  private anchorX = 0;
  private anchorY = 0;
  private moved = false;

  constructor(private readonly cell: number) {}

  down(x: number, y: number, t: number): void {
    this.start = { x, y, t };
    this.anchorX = x;
    this.anchorY = y;
    this.moved = false;
  }

  move(x: number, y: number): TouchCommand[] {
    if (!this.start) return [];
    const commands: TouchCommand[] = [];
    while (x - this.anchorX >= this.cell) {
      commands.push('move-right');
      this.anchorX += this.cell;
    }
    while (this.anchorX - x >= this.cell) {
      commands.push('move-left');
      this.anchorX -= this.cell;
    }
    // Вниз — ускорение по клетке, только если палец идёт больше вниз, чем вбок.
    while (
      y - this.anchorY >= this.cell &&
      Math.abs(y - this.start.y) > Math.abs(x - this.start.x)
    ) {
      commands.push('soft-drop');
      this.anchorY += this.cell;
    }
    if (commands.length > 0) this.moved = true;
    return commands;
  }

  up(x: number, y: number, t: number): TouchCommand[] {
    const start = this.start;
    this.start = null;
    if (!start) return [];
    const dx = x - start.x;
    const dy = y - start.y;
    const ms = Math.max(1, t - start.t);
    if (!this.moved && ms < TAP_MS && Math.hypot(dx, dy) < this.cell * 0.5)
      return ['rotate-clockwise'];
    const speed = (dy / this.cell / ms) * 1000;
    if (dy > this.cell * 1.5 && speed > FLICK_CELLS_PER_S && Math.abs(dy) > Math.abs(dx))
      return ['hard-drop'];
    return [];
  }

  cancel(): void {
    this.start = null;
  }
}

/** Подключает жесты к области банки; `send` получает команды. Возвращает отключение. */
export function attachGestures(
  target: Container,
  area: Rectangle,
  cell: number,
  send: (command: TouchCommand) => void,
): () => void {
  const tracker = new GestureTracker(cell);
  target.eventMode = 'static';
  target.hitArea = area;
  const local = (e: FederatedPointerEvent) => target.toLocal(e.global);
  const down = (e: FederatedPointerEvent) => {
    const p = local(e);
    tracker.down(p.x, p.y, performance.now());
  };
  const move = (e: FederatedPointerEvent) => {
    const p = local(e);
    tracker.move(p.x, p.y).forEach(send);
  };
  const up = (e: FederatedPointerEvent) => {
    const p = local(e);
    tracker.up(p.x, p.y, performance.now()).forEach(send);
  };
  const cancel = () => tracker.cancel();
  target.on('pointerdown', down);
  target.on('globalpointermove', move);
  target.on('pointerup', up);
  target.on('pointerupoutside', up);
  target.on('pointercancel', cancel);
  return () => {
    target.off('pointerdown', down);
    target.off('globalpointermove', move);
    target.off('pointerup', up);
    target.off('pointerupoutside', up);
    target.off('pointercancel', cancel);
  };
}

export interface PadButton {
  readonly icon: string;
  readonly label?: string;
  readonly title: string;
  readonly width: number;
  /** Удержание повторяет действие (сдвиг, ускорение). */
  readonly repeat?: boolean;
  /** Пустое место в ряду (там экран ставит своё, например кольцо способности). */
  readonly spacer?: boolean;
  readonly onPress: () => void;
}

/** Задержка до автоповтора и его шаг, мс. */
const REPEAT_DELAY = 220;
const REPEAT_EVERY = 85;

/**
 * Экранные кнопки управления: крупные (не меньше 44 × 44 px на телефоне), с автоповтором
 * при удержании. Ряды кнопок по `rows`, высота ряда — `height`.
 */
export class TouchPad extends Container {
  private timers: ReturnType<typeof setTimeout>[] = [];

  constructor(rows: readonly (readonly PadButton[])[], width: number, height: number, gap = 12) {
    super();
    rows.forEach((row, r) => {
      const total = row.reduce((sum, b) => sum + b.width, 0) + gap * (row.length - 1);
      let x = (width - total) / 2;
      for (const spec of row) {
        if (spec.spacer) {
          x += spec.width + gap;
          continue;
        }
        const button = this.button(spec, height);
        button.position.set(x, r * (height + gap));
        this.addChild(button);
        x += spec.width + gap;
      }
    });
  }

  private button(spec: PadButton, height: number): Container {
    const box = new Container();
    const face = new Container();
    const bg = new Graphics()
      .roundRect(0, 5, spec.width, height, 22)
      .fill(COLOR.ink)
      .roundRect(0, 0, spec.width, height, 22)
      .fill(vertical(0x2a527a, 0x16304f))
      .stroke({ width: 3, color: COLOR.ink });
    face.addChild(bg);
    const icon = artSprite(spec.icon, Math.min(48, height * 0.5));
    if (spec.label) {
      icon.position.set(height * 0.45, height / 2);
      const t = new Text({
        text: spec.label,
        style: textStyle({ size: 20, weight: '700', color: COLOR.textSoft }),
      });
      t.anchor.set(0, 0.5);
      t.position.set(height * 0.8, height / 2);
      face.addChild(icon, t);
    } else {
      icon.position.set(spec.width / 2, height / 2);
      face.addChild(icon);
    }
    box.addChild(face);
    box.eventMode = 'static';
    box.cursor = 'pointer';
    box.hitArea = new Rectangle(0, 0, spec.width, height + 5);
    box.accessible = true;
    box.accessibleType = 'button';
    box.accessibleTitle = spec.title;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const stop = () => {
      face.y = 0;
      if (timer) clearTimeout(timer);
      timer = null;
    };
    const tick = () => {
      spec.onPress();
      timer = setTimeout(tick, REPEAT_EVERY);
      this.timers.push(timer);
    };
    box.on('pointerdown', (e) => {
      e.stopPropagation();
      face.y = 3;
      spec.onPress();
      if (spec.repeat) {
        timer = setTimeout(tick, REPEAT_DELAY);
        this.timers.push(timer);
      }
    });
    box.on('pointerup', stop);
    box.on('pointerupoutside', stop);
    box.on('pointerleave', stop);
    box.on('pointercancel', stop);
    // Клавиатура через систему доступности Pixi: одно нажатие — одно действие.
    box.on('click', (e: { pointerType?: string }) => {
      if (e.pointerType === undefined) spec.onPress();
    });
    return box;
  }

  override destroy(): void {
    this.timers.forEach((t) => clearTimeout(t));
    this.timers = [];
    super.destroy({ children: true });
  }
}
